import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { Box, GlobalStyles, Typography } from '@mui/material';

import MetaTags from '../../components/MetaTags';
import Loading from '../../components/Loading';
import FooterMenu from '../../components/Footer/FooterMenu';
import S3Avatar from '../../components/S3Avatar';
import { callBackbonePostApi } from '../../services/backboneApiService';
import { formatCompactNumber } from '../../utils';
import { getUser } from '../../utils/cookie';

import {
  SESSION_WORK_ACTIONS,
  SESSION_WORK_COLORS,
  SESSION_WORK_COPY,
  SESSION_WORK_MILESTONES,
  SESSION_WORK_PODIUM_METALS,
  SESSION_WORK_ROUTE,
  SESSION_WORK_UNIT,
  SESSION_WORK_VALUE_KEY,
} from './constants';
import {
  buildSessionWorkStandings,
  findMilestoneSummary,
  formatMilestoneLabel,
  getBoardEntries,
  getMilestoneRoute,
  getPodiumEntries,
  getSummaryMilestones,
  parseMilestoneParam,
  resolveAvatarSrc,
} from './helpers';

const forceFieldBackground = `
  radial-gradient(ellipse 90% 55% at 50% -15%, ${SESSION_WORK_COLORS.copperGlow}, transparent 58%),
  radial-gradient(circle at 85% 20%, rgba(0, 118, 255, 0.14), transparent 42%),
  radial-gradient(circle at 12% 70%, rgba(232, 93, 42, 0.1), transparent 38%),
  linear-gradient(180deg, ${SESSION_WORK_COLORS.ink} 0%, ${SESSION_WORK_COLORS.charcoal} 42%, ${SESSION_WORK_COLORS.ink} 100%)
`;

const RankMark = ({ rank }) => (
  <Box
    sx={{
      width: 36,
      height: 36,
      borderRadius: '4px',
      display: 'grid',
      placeItems: 'center',
      fontFamily: '"Oswald", "Futura", "Arial Narrow", sans-serif',
      fontWeight: 700,
      fontSize: 16,
      letterSpacing: '0.04em',
      color: SESSION_WORK_COLORS.bone,
      background:
        rank === 1
          ? `linear-gradient(145deg, ${SESSION_WORK_COLORS.gold}, #9A7A12)`
          : rank === 2
            ? `linear-gradient(145deg, ${SESSION_WORK_COLORS.silver}, #6E757C)`
            : rank === 3
              ? `linear-gradient(145deg, ${SESSION_WORK_COLORS.bronze}, #7A4A22)`
              : SESSION_WORK_COLORS.row,
      border: `1px solid ${rank <= 3 ? 'transparent' : 'rgba(255,255,255,0.08)'}`,
    }}
  >
    {rank}
  </Box>
);

const AthleteRow = ({ entry, isCurrentUser, showLink }) => {
  const avatarSrc = resolveAvatarSrc(entry.image, entry.id);
  const content = (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '36px 48px 1fr auto',
        gap: 1.5,
        alignItems: 'center',
        px: { xs: 1.5, md: 2 },
        py: 1.25,
        background: isCurrentUser ? 'rgba(212, 138, 58, 0.12)' : SESSION_WORK_COLORS.row,
        borderLeft: isCurrentUser
          ? `3px solid ${SESSION_WORK_COLORS.copper}`
          : '3px solid transparent',
        borderBottom: '1px solid rgba(255,255,255,0.04)',
        transition: 'background 180ms ease',
        '&:hover': {
          background: 'rgba(255,255,255,0.04)',
        },
      }}
    >
      <RankMark rank={entry.rank} />
      <S3Avatar
        src={avatarSrc}
        sx={{ width: 48, height: 48, borderRadius: '6px' }}
      />
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontFamily: '"Oswald", "Futura", "Arial Narrow", sans-serif',
            fontWeight: 600,
            fontSize: { xs: 15, md: 17 },
            letterSpacing: '0.03em',
            color: SESSION_WORK_COLORS.bone,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {entry.fullName || entry.username}
        </Typography>
        <Typography sx={{ color: SESSION_WORK_COLORS.muted, fontSize: 13 }}>
          @{entry.username}
        </Typography>
      </Box>
      <Typography
        sx={{
          fontFamily: '"Oswald", "Futura", "Arial Narrow", sans-serif',
          fontWeight: 700,
          fontSize: { xs: 16, md: 20 },
          color: SESSION_WORK_COLORS.copper,
          whiteSpace: 'nowrap',
        }}
      >
        {formatCompactNumber(entry.value)}
        <Box
          component="span"
          sx={{
            ml: 0.75,
            fontSize: 11,
            fontWeight: 500,
            color: SESSION_WORK_COLORS.muted,
            letterSpacing: '0.08em',
          }}
        >
          {SESSION_WORK_UNIT}
        </Box>
      </Typography>
    </Box>
  );

  if (!showLink || !entry.username) {
    return content;
  }

  return (
    <Box
      component={RouterLink}
      to={`/member/${entry.username}`}
      sx={{ textDecoration: 'none', display: 'block' }}
    >
      {content}
    </Box>
  );
};

const MilestoneHub = ({ summary, isLoading, loadError }) => {
  const milestones = useMemo(() => {
    const fromApi = getSummaryMilestones(summary);
    return SESSION_WORK_MILESTONES.map((threshold) => {
      const match = findMilestoneSummary(fromApi, threshold);
      return {
        threshold,
        label: formatMilestoneLabel(threshold),
        count: Number(match?.count) || 0,
        topEntry: match?.topEntry || null,
        route: getMilestoneRoute(threshold),
      };
    });
  }, [summary]);

  return (
    <Box>
      <Box
        sx={{
          maxWidth: 960,
          mx: 'auto',
          px: { xs: 2, md: 3 },
          pt: { xs: 6, md: 9 },
          pb: 4,
        }}
      >
        <Typography
          sx={{
            fontFamily: '"Oswald", "Futura", "Arial Narrow", sans-serif',
            fontSize: 13,
            letterSpacing: '0.28em',
            color: SESSION_WORK_COLORS.copper,
            mb: 1.5,
          }}
        >
          {SESSION_WORK_COPY.BRAND_LOCKUP}
        </Typography>
        <Typography
          component="h1"
          sx={{
            fontFamily: '"Oswald", "Futura", "Arial Narrow", sans-serif',
            fontWeight: 700,
            fontSize: { xs: 48, md: 72 },
            lineHeight: 0.95,
            letterSpacing: '0.02em',
            color: SESSION_WORK_COLORS.bone,
            mb: 2,
          }}
        >
          {SESSION_WORK_COPY.HEADLINE}
        </Typography>
        <Typography
          sx={{
            maxWidth: 520,
            color: SESSION_WORK_COLORS.steel,
            fontSize: { xs: 16, md: 18 },
            lineHeight: 1.5,
            mb: 3,
          }}
        >
          {SESSION_WORK_COPY.PAGE_DESCRIPTION}
        </Typography>
        <Box
          component="a"
          href="#milestone-ladder"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            px: 2.5,
            py: 1.25,
            background: SESSION_WORK_COLORS.copper,
            color: SESSION_WORK_COLORS.ink,
            fontFamily: '"Oswald", "Futura", "Arial Narrow", sans-serif',
            fontWeight: 700,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            textDecoration: 'none',
            borderRadius: '2px',
            transition: 'transform 180ms ease, background 180ms ease',
            '&:hover': {
              background: SESSION_WORK_COLORS.copperDeep,
              transform: 'translateY(-1px)',
            },
          }}
        >
          {SESSION_WORK_COPY.CTA}
        </Box>
      </Box>

      <Box
        sx={{
          maxWidth: 960,
          mx: 'auto',
          px: { xs: 2, md: 3 },
          py: { xs: 4, md: 6 },
        }}
      >
        <Typography
          sx={{
            fontFamily: '"Oswald", "Futura", "Arial Narrow", sans-serif',
            fontWeight: 700,
            fontSize: 22,
            letterSpacing: '0.08em',
            color: SESSION_WORK_COLORS.bone,
            mb: 1,
          }}
        >
          {SESSION_WORK_COPY.MISSION_TITLE}
        </Typography>
        <Typography sx={{ color: SESSION_WORK_COLORS.steel, maxWidth: 640, mb: 4, lineHeight: 1.55 }}>
          {SESSION_WORK_COPY.MISSION_BODY}
        </Typography>

        <Typography
          id="milestone-ladder"
          sx={{
            fontFamily: '"Oswald", "Futura", "Arial Narrow", sans-serif',
            fontWeight: 700,
            fontSize: 22,
            letterSpacing: '0.08em',
            color: SESSION_WORK_COLORS.bone,
            mb: 2.5,
            scrollMarginTop: 96,
          }}
        >
          {SESSION_WORK_COPY.LADDER_TITLE}
        </Typography>

        {isLoading && (
          <Box sx={{ py: 6 }}>
            <Loading />
          </Box>
        )}

        {!isLoading && loadError && (
          <Typography sx={{ color: SESSION_WORK_COLORS.ember, mb: 3 }}>{loadError}</Typography>
        )}

        {!isLoading && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.25,
            }}
          >
            {milestones.map((milestone, index) => (
              <Box
                key={milestone.threshold}
                component={RouterLink}
                to={milestone.route}
                className="session-work-motion"
                sx={{
                  textDecoration: 'none',
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '120px 1fr auto' },
                  gap: { xs: 1, md: 2 },
                  alignItems: 'center',
                  px: { xs: 2, md: 2.5 },
                  py: { xs: 2, md: 2.25 },
                  background: SESSION_WORK_COLORS.panel,
                  border: '1px solid rgba(255,255,255,0.06)',
                  borderLeft: `4px solid ${SESSION_WORK_COLORS.copper}`,
                  opacity: 0,
                  animation: `sessionWorkRise 520ms ease forwards`,
                  animationDelay: `${index * 70}ms`,
                  transition: 'border-color 180ms ease, transform 180ms ease',
                  '&:hover': {
                    borderColor: 'rgba(212, 138, 58, 0.45)',
                    transform: 'translateX(4px)',
                  },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: '"Oswald", "Futura", "Arial Narrow", sans-serif',
                    fontWeight: 700,
                    fontSize: { xs: 32, md: 36 },
                    color: SESSION_WORK_COLORS.copper,
                    letterSpacing: '0.02em',
                    lineHeight: 1,
                  }}
                >
                  {milestone.label}
                </Typography>
                <Box>
                  <Typography
                    sx={{
                      color: SESSION_WORK_COLORS.bone,
                      fontWeight: 600,
                      fontSize: 16,
                      mb: 0.25,
                    }}
                  >
                    {milestone.count === 1
                      ? '1 athlete has cleared this'
                      : `${milestone.count} athletes have cleared this`}
                  </Typography>
                  <Typography sx={{ color: SESSION_WORK_COLORS.muted, fontSize: 13 }}>
                    {milestone.topEntry?.username
                      ? `Leading: @${milestone.topEntry.username} · ${formatCompactNumber(
                          milestone.topEntry.sessionWork
                        )} ${SESSION_WORK_UNIT}`
                      : 'Waiting for the first clear'}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontFamily: '"Oswald", "Futura", "Arial Narrow", sans-serif',
                    letterSpacing: '0.14em',
                    color: SESSION_WORK_COLORS.copper,
                    fontSize: 13,
                    fontWeight: 700,
                    textAlign: { xs: 'left', md: 'right' },
                  }}
                >
                  VIEW BOARD →
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        <Typography sx={{ color: SESSION_WORK_COLORS.muted, fontSize: 13, mt: 4 }}>
          {SESSION_WORK_COPY.SOURCE_NOTE}
        </Typography>
      </Box>
    </Box>
  );
};

const MilestoneDetail = ({ milestone, entries, isLoading, loadError }) => {
  const user = getUser();
  const label = formatMilestoneLabel(milestone);
  const standings = useMemo(
    () => buildSessionWorkStandings(entries, SESSION_WORK_VALUE_KEY),
    [entries]
  );
  const podium = useMemo(() => getPodiumEntries(standings, 3), [standings]);
  const currentUsername = String(user?.username || '').toLowerCase();

  return (
    <Box>
      <Box
        sx={{
          maxWidth: 960,
          mx: 'auto',
          px: { xs: 2, md: 3 },
          pt: { xs: 5, md: 7 },
          pb: 3,
        }}
      >
        <Box
          component={RouterLink}
          to={SESSION_WORK_ROUTE}
          sx={{
            display: 'inline-block',
            mb: 2.5,
            color: SESSION_WORK_COLORS.copper,
            textDecoration: 'none',
            fontFamily: '"Oswald", "Futura", "Arial Narrow", sans-serif',
            letterSpacing: '0.12em',
            fontSize: 13,
            fontWeight: 600,
            '&:hover': { color: SESSION_WORK_COLORS.bone },
          }}
        >
          ← ALL MILESTONES
        </Box>
        <Typography
          sx={{
            fontFamily: '"Oswald", "Futura", "Arial Narrow", sans-serif',
            fontSize: 13,
            letterSpacing: '0.28em',
            color: SESSION_WORK_COLORS.copper,
            mb: 1,
          }}
        >
          {SESSION_WORK_COPY.BRAND_LOCKUP}
        </Typography>
        <Typography
          component="h1"
          sx={{
            fontFamily: '"Oswald", "Futura", "Arial Narrow", sans-serif',
            fontWeight: 700,
            fontSize: { xs: 44, md: 64 },
            lineHeight: 0.95,
            color: SESSION_WORK_COLORS.bone,
            mb: 1,
          }}
        >
          {label}
        </Typography>
        <Typography sx={{ color: SESSION_WORK_COLORS.steel, maxWidth: 540, mb: 1 }}>
          Athletes who hit {label} {SESSION_WORK_UNIT} or more in a single session, ranked by their best.
        </Typography>
        <Typography sx={{ color: SESSION_WORK_COLORS.muted, fontSize: 14 }}>
          {standings.length} {standings.length === 1 ? 'athlete' : 'athletes'} on this board
        </Typography>
      </Box>

      <Box sx={{ maxWidth: 960, mx: 'auto', px: { xs: 2, md: 3 }, pb: 8 }}>
        {isLoading && (
          <Box sx={{ py: 6 }}>
            <Loading />
          </Box>
        )}

        {!isLoading && loadError && (
          <Typography sx={{ color: SESSION_WORK_COLORS.ember }}>{loadError}</Typography>
        )}

        {!isLoading && !loadError && standings.length === 0 && (
          <Box
            sx={{
              py: 6,
              px: 3,
              background: SESSION_WORK_COLORS.panel,
              border: '1px solid rgba(255,255,255,0.06)',
              textAlign: 'center',
            }}
          >
            <Typography
              sx={{
                fontFamily: '"Oswald", "Futura", "Arial Narrow", sans-serif',
                fontSize: 28,
                color: SESSION_WORK_COLORS.bone,
                mb: 1,
              }}
            >
              {SESSION_WORK_COPY.EMPTY_TITLE}
            </Typography>
            <Typography sx={{ color: SESSION_WORK_COLORS.steel }}>
              {SESSION_WORK_COPY.EMPTY_BODY}
            </Typography>
          </Box>
        )}

        {!isLoading && podium.length > 0 && (
          <Box sx={{ mb: 5 }}>
            <Typography
              sx={{
                fontFamily: '"Oswald", "Futura", "Arial Narrow", sans-serif',
                fontWeight: 700,
                letterSpacing: '0.1em',
                color: SESSION_WORK_COLORS.bone,
                mb: 2.5,
              }}
            >
              TOP CLEARERS
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                gap: 1.5,
              }}
            >
              {SESSION_WORK_PODIUM_METALS.map((metal) => {
                const entry = podium.find((item) => item.rank === metal.rank);
                if (!entry) {
                  return null;
                }
                return (
                  <Box
                    key={metal.rank}
                    className="session-work-motion"
                    component={RouterLink}
                    to={`/member/${entry.username}`}
                    sx={{
                      textDecoration: 'none',
                      p: 2.5,
                      background: SESSION_WORK_COLORS.panel,
                      borderTop: `3px solid ${metal.color}`,
                      opacity: 0,
                      animation: 'sessionWorkRise 600ms ease forwards',
                      animationDelay: `${(metal.rank - 1) * 90}ms`,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: '"Oswald", "Futura", "Arial Narrow", sans-serif',
                        fontSize: 12,
                        letterSpacing: '0.18em',
                        color: metal.color,
                        mb: 1.5,
                      }}
                    >
                      {metal.label}
                    </Typography>
                    <S3Avatar
                      src={resolveAvatarSrc(entry.image, entry.id)}
                      sx={{ width: 72, height: 72, borderRadius: '8px', mb: 1.5 }}
                    />
                    <Typography
                      sx={{
                        fontFamily: '"Oswald", "Futura", "Arial Narrow", sans-serif',
                        fontWeight: 700,
                        fontSize: 20,
                        color: SESSION_WORK_COLORS.bone,
                      }}
                    >
                      {entry.fullName || entry.username}
                    </Typography>
                    <Typography sx={{ color: SESSION_WORK_COLORS.muted, fontSize: 13, mb: 1 }}>
                      @{entry.username}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: '"Oswald", "Futura", "Arial Narrow", sans-serif',
                        fontWeight: 700,
                        fontSize: 24,
                        color: SESSION_WORK_COLORS.copper,
                      }}
                    >
                      {formatCompactNumber(entry.value)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {!isLoading && standings.length > 0 && (
          <Box>
            <Typography
              sx={{
                fontFamily: '"Oswald", "Futura", "Arial Narrow", sans-serif',
                fontWeight: 700,
                letterSpacing: '0.1em',
                color: SESSION_WORK_COLORS.bone,
                mb: 1.5,
              }}
            >
              {SESSION_WORK_COPY.STANDINGS_TITLE}
            </Typography>
            <Box
              sx={{
                border: '1px solid rgba(255,255,255,0.06)',
                overflow: 'hidden',
              }}
            >
              {standings.map((entry) => (
                <AthleteRow
                  key={`${entry.id || entry.username}-${entry.rank}`}
                  entry={entry}
                  isCurrentUser={
                    !!currentUsername &&
                    String(entry.username || '').toLowerCase() === currentUsername
                  }
                  showLink
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const SessionWorkMilestones = () => {
  const navigate = useNavigate();
  const { milestone: milestoneParam } = useParams();
  const selectedMilestone = parseMilestoneParam(milestoneParam);

  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);

  useEffect(() => {
    if (milestoneParam && selectedMilestone == null) {
      navigate(SESSION_WORK_ROUTE, { replace: true });
    }
  }, [milestoneParam, selectedMilestone, navigate]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setLoadError('');

      try {
        if (selectedMilestone) {
          const response = await callBackbonePostApi(
            {
              action: SESSION_WORK_ACTIONS.MILESTONE_BOARD,
              milestone: selectedMilestone,
            },
            ''
          );
          if (cancelled) {
            return;
          }
          if (response?.status !== 200) {
            setLoadError('Could not load this milestone board.');
            setEntries([]);
          } else {
            setEntries(getBoardEntries(response?.data, 'entries'));
          }
        } else {
          const response = await callBackbonePostApi(
            {
              action: SESSION_WORK_ACTIONS.SUMMARY,
            },
            ''
          );
          if (cancelled) {
            return;
          }
          if (response?.status !== 200) {
            setLoadError('Could not load session work milestones.');
            setSummary(null);
          } else {
            setSummary(response?.data || null);
          }
        }
      } catch (error) {
        if (!cancelled) {
          setLoadError('Could not load session work milestones.');
          setSummary(null);
          setEntries([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [selectedMilestone]);

  const pageTitle = selectedMilestone
    ? `Black Box VR — ${formatMilestoneLabel(selectedMilestone)} Session Work`
    : SESSION_WORK_COPY.PAGE_TITLE;

  return (
    <Box sx={{ minHeight: '100vh', background: forceFieldBackground, color: SESSION_WORK_COLORS.bone }}>
      <MetaTags title={pageTitle} description={SESSION_WORK_COPY.PAGE_DESCRIPTION} />
      <GlobalStyles
        styles={{
          '@keyframes sessionWorkRise': {
            from: { opacity: 0, transform: 'translateY(14px)' },
            to: { opacity: 1, transform: 'translateY(0)' },
          },
          '@keyframes sessionWorkGlow': {
            '0%, 100%': { opacity: 0.55 },
            '50%': { opacity: 1 },
          },
          '.session-work-motion': {
            '@media (prefers-reduced-motion: reduce)': {
              animation: 'none !important',
              opacity: '1 !important',
              transform: 'none !important',
            },
          },
        }}
      />

      <Box
        className="session-work-motion"
        sx={{
          height: 3,
          background: `linear-gradient(90deg, transparent, ${SESSION_WORK_COLORS.copper}, ${SESSION_WORK_COLORS.blue}, transparent)`,
          animation: 'sessionWorkGlow 3.2s ease-in-out infinite',
        }}
      />

      {selectedMilestone ? (
        <MilestoneDetail
          milestone={selectedMilestone}
          entries={entries}
          isLoading={isLoading}
          loadError={loadError}
        />
      ) : (
        <MilestoneHub summary={summary} isLoading={isLoading} loadError={loadError} />
      )}

      <FooterMenu />
    </Box>
  );
};

export default SessionWorkMilestones;
